/**
 * GameModeSelectScreen - Choose between Slot Machine and Pac-Man modes
 */

import { useEffect, useCallback, useRef } from 'react';
import { useGame, type GameMode } from '../context/GameContext';
import slotPreview from '../assets/slot_cartridge.png';
import pacmanPreview from '../assets/pacman_cartridge.jpeg';
import './GameModeSelectScreen.css';

export function GameModeSelectScreen() {
  const { state, actions } = useGame();
  const { gameMode, selectedSprint, members } = state;
  const isSelectingRef = useRef(false);

  // Reset selection flag when component mounts (in case of navigation back)
  useEffect(() => {
    isSelectingRef.current = false;
  }, []);

  const selectMode = useCallback((mode: GameMode) => {
    // Prevent double selection (React StrictMode causes double mount)
    if (isSelectingRef.current) return;
    isSelectingRef.current = true;
    actions.setGameMode(mode);
    actions.setPhase(mode === 'SLOT' ? 'SPIN' : 'PACMAN_MAZE');
  }, [actions]);

  // Keyboard controls
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.code === 'KeyA' || e.code === 'Digit1') {
        selectMode('SLOT');
      } else if (e.code === 'KeyB' || e.code === 'Digit2') {
        selectMode('PACMAN');
      } else if (e.code === 'Escape') {
        actions.setPhase('BOOT');
      }
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectMode, actions]);

  return (
    <div className="game-mode-screen">
      <header className="game-mode-screen__header">
        <h1>★ EASY DAILY ★</h1>
        <p>
          Sprint {selectedSprint?.name} • {members.length} participants
        </p>
      </header>

      <h2 className="game-mode-screen__title">CHOOSE YOUR MODE</h2>

      <div className="game-mode-cards">
        <button
          className={`game-mode-card ${gameMode === 'SLOT' ? 'game-mode-card--selected' : ''}`}
          onClick={() => selectMode('SLOT')}
        >
          <img src={slotPreview} alt="Slot Machine" />
          <h3>SLOT MACHINE</h3>
          <p>Quick random draw</p>
          <span className="key-hint">[A] SELECT</span>
        </button>

        <button
          className={`game-mode-card ${gameMode === 'PACMAN' ? 'game-mode-card--selected' : ''}`}
          onClick={() => selectMode('PACMAN')}
        >
          <img src={pacmanPreview} alt="Daily-Man" />
          <h3>DAILY-MAN</h3>
          <p>Ghost chase</p>
          <span className="key-hint">[B] SELECT</span>
        </button>
      </div>

      <footer className="game-mode-screen__footer">[ESC] Back</footer>
    </div>
  );
}
